import React, { useMemo, useEffect, useRef } from 'react'
import { Box, List, ListItem, ListItemText, Typography, Divider, useTheme, useMediaQuery } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { ListAlt } from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../store/redux/hooks'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import { useGetUsersWorklogQuery } from '../../store/redux/reducers/worklogSlice'
import { monthPickerActions, getDailyDate } from '../../store/redux/reducers/monthPickerSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import Loading from '../../components/Loading/Loading'
import MasonryTile from '../../components/Masonry/MasonryTile'
import MasonryTileTitle from '../../components/Masonry/MasonryTileTitle'
import { t } from 'i18next'
import { WorkDay, WorkEntry } from '../../store/types'
import MonthPicker from '../../components/MonthPicker/ReduxMonthPicker'
import { getDurationString } from '../../store/utils/worklogUtils'

const today = dayjs().startOf('day')
const fallbackDate = (): Dayjs => (today.day() === 1 ? today.subtract(3, 'day') : today.subtract(1, 'day'))

const WorklogDailyEntries = () => {
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down(500))
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(getCurrentUser)
  const employeeId = currentUser?.id || ''
  const selectedDateString = useAppSelector(getDailyDate)
  const selectedDate = dayjs(selectedDateString)
  const isSelectedDateValid = selectedDate.isValid()
  const baseMonth = isSelectedDateValid ? selectedDate : dayjs()
  const monthString = baseMonth.format(DateFormat.YearMonth)
  const prevMonthString = baseMonth.subtract(1, 'month').format(DateFormat.YearMonth)
  const currentMonthString = today.format(DateFormat.YearMonth)
  const shouldFetchCurrentMonth = monthString !== currentMonthString

  const { data: worklog, isLoading: isLoadingCurrent } = useGetUsersWorklogQuery(
    { employeeId, date: monthString },
    { skip: !employeeId }
  )

  const { data: prevWorklog, isLoading: isLoadingPrev } = useGetUsersWorklogQuery(
    { employeeId, date: prevMonthString },
    { skip: !employeeId }
  )

  const { data: currentWorklog, isLoading: isLoadingCurrentMonth } = useGetUsersWorklogQuery(
    { employeeId, date: currentMonthString },
    { skip: !employeeId || !shouldFetchCurrentMonth }
  )

  const autoSyncRef = useRef(true)
  const lastAutoDateRef = useRef<Dayjs | null>(null)

  useEffect(() => {
    if (lastAutoDateRef.current && !selectedDate.isSame(lastAutoDateRef.current, 'day')) {
      autoSyncRef.current = false
    }
  }, [selectedDate])

  useEffect(() => {
    if (!autoSyncRef.current) return
    if (isLoadingCurrent || isLoadingPrev || (shouldFetchCurrentMonth && isLoadingCurrentMonth)) return
    if (!worklog || !prevWorklog) return
    if (shouldFetchCurrentMonth && !currentWorklog) return

    const allLogs = [
      ...(worklog || []),
      ...(shouldFetchCurrentMonth ? currentWorklog || [] : []),
      ...(prevWorklog || []),
    ]

    const lastFilled = [...allLogs]
      .filter(
        d =>
          !dayjs(d.date).isAfter(today) &&
          !dayjs(d.date).isSame(today, 'day') &&
          d.entries.some(e => Number(e.hours) > 0)
      )
      .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())[0]

    const newDate = lastFilled ? dayjs(lastFilled.date) : fallbackDate()

    if (!selectedDate.isSame(newDate, 'day')) {
      dispatch(monthPickerActions.setDailyDate(newDate.toString()))
      lastAutoDateRef.current = newDate
    }
  }, [
    isLoadingCurrent,
    isLoadingPrev,
    isLoadingCurrentMonth,
    worklog,
    currentWorklog,
    prevWorklog,
    dispatch,
    selectedDate,
    shouldFetchCurrentMonth,
  ])

  const combinedWorklog = useMemo(
    () => [...(worklog || []), ...(shouldFetchCurrentMonth ? currentWorklog || [] : [])],
    [worklog, currentWorklog, shouldFetchCurrentMonth]
  )

  const workEntries: WorkEntry[] = useMemo(() => {
    if (!combinedWorklog.length || !isSelectedDateValid) return []
    const day: WorkDay | undefined = combinedWorklog.find(w => w.date === selectedDate.format('YYYY-MM-DD'))
    return day ? day.entries : []
  }, [combinedWorklog, selectedDate, isSelectedDateValid])

  const groupedEntries = useMemo(() => {
    const map = new Map<string, WorkEntry[]>()
    workEntries.forEach(entry => {
      const key = entry.project.id || entry.project.name
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(entry)
    })
    return [...map.entries()].sort((a, b) => a[1][0].project.name.localeCompare(b[1][0].project.name, 'pl'))
  }, [workEntries])

  return (
    <MasonryTile sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          width: '100%',
          flexDirection: isDownSm ? 'column' : 'row',
          gap: isDownSm ? 1 : 0,
        }}
      >
        <MasonryTileTitle title={t('components.tile.title.dailyWorklog')} icon={ListAlt} />
        <MonthPicker isDailyRange />
      </Box>

      {isLoadingCurrent ? (
        <Loading />
      ) : groupedEntries.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', mt: 2 }}>
          {t('components.dailyWorklog.noData')}
        </Typography>
      ) : (
        <List dense>
          {groupedEntries.map(([projectKey, entries], gIdx) => {
            const totalHours = entries.reduce((sum, e) => sum + Number(e.hours), 0)
            return (
              <React.Fragment key={projectKey}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'text.highEmphasis', fontWeight: 600, pt: gIdx === 0 ? 0 : 1 }}
                >
                  {entries[0].project.name} · {getDurationString(totalHours)}
                </Typography>
                {entries.map((entry, idx) => (
                  <React.Fragment key={entry.id}>
                    <ListItem disableGutters sx={{ pl: 1 }}>
                      <ListItemText
                        primary={getDurationString(entry.hours)}
                        secondary={entry.description}
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      />
                    </ListItem>
                    {idx < entries.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
                {gIdx < groupedEntries.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            )
          })}
        </List>
      )}
    </MasonryTile>
  )
}

export default WorklogDailyEntries
